/**
 * 🎬 The ManagedVimeoPlayer - A Bridge Between Worlds
 *
 * "Where Vimeo iframes dance with our audio player in harmony,
 * ensuring only one maestro conducts at a time. When video plays,
 * music bows gracefully aside; a cosmic coordination of media."
 *
 * - The Theatrical Video Virtuoso 🎭✨
 */

import { useEffect, useRef } from 'react';
import Player from '@vimeo/player';
import mediaManager from '../utils/mediaManager';

/**
 * 🌟 ManagedVimeoPlayer - Vimeo iframe with mediaManager integration
 *
 * This component wraps a Vimeo iframe and registers it with our mediaManager,
 * ensuring that when the video plays, other media (like our music player) pauses.
 * Previously, Vimeo iframes were invisible to mediaManager since it only detected
 * native <audio> and <video> elements - not iframes! 😱
 *
 * @param {string} videoUrl - Full Vimeo player URL (e.g., "https://player.vimeo.com/video/123?h=abc")
 * @param {string} title - iframe title for accessibility
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {object} props - Additional props passed to the iframe
 */
const ManagedVimeoPlayer = ({
  videoUrl,
  title = 'vimeo-player',
  className = '',
  style = {},
  ...props
}) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  // 🔮 Generate a unique player ID for mediaManager registration
  const playerId = useRef(`vimeo-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`);

  useEffect(() => {
    if (!iframeRef.current) return;

    // 📝 Copy ref value to local variable for cleanup function (ESLint react-hooks/exhaustive-deps)
    const currentPlayerId = playerId.current;

    // 🎭 Initialize the Vimeo Player SDK
    const player = new Player(iframeRef.current);
    playerRef.current = player;

    // 🎵 When Vimeo starts playing → Tell mediaManager to pause others (including our music!)
    const handlePlay = () => {
      console.log(`🎬 Vimeo player ${currentPlayerId} started - requesting mediaManager to pause others`);
      mediaManager.requestPlay(currentPlayerId, true); // true = pause other players
    };

    // ⏸️ When Vimeo pauses → Update mediaManager state
    const handlePause = () => {
      console.log(`⏸️ Vimeo player ${currentPlayerId} paused`);
      mediaManager.pausePlayer(currentPlayerId);
    };

    // 🔚 When video ends → Update mediaManager state
    const handleEnded = () => {
      console.log(`🔚 Vimeo player ${currentPlayerId} ended`);
      mediaManager.pausePlayer(currentPlayerId);
    };

    // 📝 Register event listeners with Vimeo SDK
    player.on('play', handlePlay);
    player.on('pause', handlePause);
    player.on('ended', handleEnded);

    // 🎭 Register a "virtual player" with mediaManager
    // We can't control the iframe directly like native elements, but we can track its state
    // and ensure other players pause when this one plays
    mediaManager.players.set(currentPlayerId, {
      element: iframeRef.current,
      type: 'video',
      isPlaying: false,
      // 🔮 Custom pause handler since we need to use Vimeo SDK to pause
      pauseViaSDK: () => {
        player.pause().catch(err => console.warn('Could not pause Vimeo:', err));
      }
    });

    // 🧹 Cleanup - The grand finale curtain call (but a gentle one!)
    // ⚠️ Do NOT call player.destroy() here — it physically removes the iframe
    // from the DOM via parentNode.removeChild(), which conflicts with React's
    // virtual DOM management. React 18 StrictMode double-fires effects, so
    // destroy() would nuke the iframe on the first simulated unmount and
    // leave us with a ghost reference. Let React own the DOM lifecycle! 🎭
    return () => {
      player.off('play', handlePlay);
      player.off('pause', handlePause);
      player.off('ended', handleEnded);
      mediaManager.unregisterPlayer(currentPlayerId);
    };
  }, [videoUrl]); // Re-init if video URL changes

  return (
    <iframe
      ref={iframeRef}
      src={videoUrl}
      title={title}
      className={className}
      style={style}
      allowFullScreen
      {...props}
    />
  );
};

export default ManagedVimeoPlayer;
